// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## ChordQualityAndInversionET.js
// Identification of chords by letter name, quality, inversion &/or figures
//
// See constructor for options.
//
// Some gotchas: if just soprano_factor or inversion is being tested, only non-symmetrical
// harmonies are given, and they are all given in open spacing.
//
import { sample } from 'shared/src/utils/sample';
import { weightedSample } from 'shared/src/utils/weightedSample';
import { makeString } from 'shared/src/knowledge';
import { isObject } from 'shared/src/utils/isObject';
import type { ChordQualityName } from 'shared/definitions/musicTheory/chords';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import type { InteractiveOptions, UserData } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import Chord from '../uTheory.Notation/chord';
import type { LetterPanelOptions } from '../uTheory.Notation/letterPanel';
import type { ChordIdButtonsOptions } from './chordIdButtons';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import type { MidiMessage } from '../uTheory.Notation/soundBufferHolder';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';

const triadSkills = [
  'major_triad_et',
  'minor_triad_et',
  'diminshed_triad_et',
  'augmented_triad_et',
  'arp_triad_et',
  'close_triad_et',
  'open_triad_et',
  'triad_inversions_et',
  'triad_soprano_factor_et',
] as const;

type TriadETSkill = typeof triadSkills[number];

const triadQualitySkills = triadSkills.slice(0, 4);

const seventhSkills = [
  'maj_7_chords_et',
  'min_7_chords_et',
  'dom_7_chords_et',
  'half_dim_7_chords_et',
  'dim_7_chords_et',
  'arp_7_chords_et',
  'close_7_chords_et',
  'open_7_chords_et',
  'seventh_chord_inversions_et',
  'seventh_chord_soprano_factor',
] as const;

type SeventhChordETSkill = typeof seventhSkills[number];

const seventhQualitySkills = seventhSkills.slice(0, 5);

const inversionSkills = ['triad_inversions_et', 'seventh_chord_inversions_et'] as const;

type InversionETSkill = typeof inversionSkills[number];

const sopranoFactorSkills = ['seventh_chord_soprano_factor', 'triad_soprano_factor_et'] as const;

type SopranoFactorETSkill = typeof sopranoFactorSkills[number];

type ChordETSkill = TriadETSkill | SeventhChordETSkill | InversionETSkill | SopranoFactorETSkill;

interface ChordQualityAndInversionETOptions extends InteractiveOptions {
  /**
   * questions: ['F#3 Dom 42/open'] 'open', 'close', 'arp'
   * - in format: '{chord name}/{open | close | arp}'
   */
  questions?: string[];
  /** Required if drill===false, otherwise can be determined from skills */
  inversion?: 'numbers' | 'figures' | false;
  /** Required if drill===false, otherwise can be determined from skills */
  sopranoFactor?: boolean;
  /** Required if drill===false, otherwise can be determined from skills */
  chordTypes?: 'triads' | 'sevenths' | 'both' | false;
  /** Should we test the chord quality? */
  quality?: boolean;
}

const chordPlaybackTypes = ['open', 'close', 'arp', 'byAbility'] as const;
type ChordPlaybackType = typeof chordPlaybackTypes[number];

interface ChordQualityAndInversionETQuestion {
  chord: Chord;
  spacing: ChordPlaybackType;
  noteHolder: NoteHolder;
  midiMessages: MidiMessage[];
  text: number;
  skills: ChordETSkill[];
}

export default class ChordQualityAndInversionET extends InteractivePrototype {
  declare options: ChordQualityAndInversionETOptions;

  declare currentQuestion: ChordQualityAndInversionETQuestion;

  declare questions: ChordQualityAndInversionETQuestion[];

  chordSettings!: {
    chordTypes: ChordQualityAndInversionETOptions['chordTypes'];
    includeQuality: boolean;
    inversion: ChordQualityAndInversionETOptions['inversion'];
    sopranoFactor: boolean;
  };

  private qg: QuestionGenerator;

  /**
   * Aural identificatione exercise for chord qualities and inversions
   *
   * @param {HTMLDivElement} displayDiv
   * @param {object} options {
   *  drill: bool, false
   *  skills: [array]
   *  questions: ['F#3 Dom 42/open'] 'open', 'close', 'arp'
   *  *** In manual questions & checkpoints, the following 3 MUST be set ***
   *  inversion: 'numbers', 'figures', 'false' // or determined from skills
   *  sopranoFactor: bool, false // or determined from skills
   *  chordTypes: 'triads', 'sevenths', 'both', false // or determined from skills;
   *    if quality is not required, this must be set to false in options; otherwise
   *    it'll be assumed to be required
   * }
   */
  constructor(
    displayDiv: HTMLDivElement,
    options: ChordQualityAndInversionETOptions,
    userData: UserData
  ) {
    super(displayDiv, options, userData);
    this.targetSpeedFromSkills = [...triadSkills, ...seventhSkills];
    this.qg = new QuestionGenerator();
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: ChordQualityAndInversionETOptions, userData: UserData) {
    this.settings = {
      instructions: true,
      letters: {
        playbackButtons: {
          noteOrNotes: 'Chord',
          limitRepetitions: options.testMode ? 2 : false,
          playbackHotkey: ['C', 'c'],
        },
        chordIdButtons: {
          // these are overridden in finishButtonSettings().
          inversionType: options.inversion,
          chordTypes: options.chordTypes || 'both',
          quality: options.chordTypes !== false,
          sopranoFactor: options.sopranoFactor,
          root: false,
        },
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
      loadAudioFirst: true,
    };
    this.finishButtonSettings(options);
    return super.init(displayDiv, options, userData);
  }

  /*
   *  inversion: 'numbers', 'figures', 'false' // or determined from skills
   *  sopranoFactor: bool, false // or determined from skills
   *  chordTypes: 'triads', 'sevenths', 'both', false // or determined from skills
   */
  finishButtonSettings(options: ChordQualityAndInversionETOptions) {
    let inversion: ChordQualityAndInversionETOptions['inversion'] = false;
    let chordTypes: ChordQualityAndInversionETOptions['chordTypes'] = false;
    let sopranoFactor: ChordQualityAndInversionETOptions['sopranoFactor'] = false;
    const includeQuality = !(options.chordTypes === false);
    // If manual questions...
    if (!options.drill) {
      chordTypes = (includeQuality && options.chordTypes) || 'both'; // to be safe
      inversion = options.inversion ? options.inversion : false;
      sopranoFactor = !!options.sopranoFactor;
    } else {
      if (!options.skills || !options.skills.length) {
        throw Error(
          'options.skills must be provided when options.drill is true in ChordQualityAndInversionET'
        );
      }
      // Otherwise, we're drill questions
      const skills = options.skills.map(makeString);
      inversion = skills.find((s) => inversionSkills.includes(s as any))
        ? sample(['figures', 'numbers'] as const)
        : false;
      sopranoFactor = !!skills.find((s) => sopranoFactorSkills.includes(s as any));
      const includeTriads = !!skills.find((s) => triadSkills.includes(s as any));
      const includeSevenths = !!skills.find((s) => seventhSkills.includes(s as any));
      if (includeTriads && includeSevenths) {
        chordTypes = 'both';
      } else if (includeTriads) {
        chordTypes = 'triads';
      } else if (includeSevenths) {
        chordTypes = 'sevenths';
      } else {
        chordTypes = false;
      }
    }

    this.chordSettings = {
      chordTypes,
      includeQuality,
      inversion,
      sopranoFactor,
    };

    utils.merge(
      (this.settings.letters as LetterPanelOptions).chordIdButtons as ChordIdButtonsOptions,
      {
        inversionType: inversion,
        chordTypes,
        quality: includeQuality,
        sopranoFactor,
        root: false,
      }
    );
  }

  /**
   * generate questions for interactive
   *
   * @param {string[]} questions optional
   * @returns {object[]} question = {
   *   chord: chord,
   *   midiMessages: [midiMessages],
   *   text: [questionNumber],
   *   skills: [skills],
   * }
   */
  generateQuestions(questions = this.options.questions) {
    if (this.options.drill) {
      this.questions = this.generateDrillQuestions();
    } else {
      questions = questions || this.options.questions;
      this.questions = this.processManualQuestions(questions);
    }
    this.questionNumber = 0;
    this.questionPrompt = this.prepareQuestionPrompt();
    return this.questions;
  }

  /**
   * @returns ['Bb Maj 7/open']
   */
  generateDrillQuestions(): ChordQualityAndInversionETQuestion[] {
    // here, we need to be sure there are at least several triad &
    // several seventh chord quality questions:
    if (!this.options.skills || !this.options.skills.length) {
      throw Error('options.skills must be set for drill questions.');
    }

    const skills = this.options.skills.map(makeString);
    const triads = triadQualitySkills.filter((skill) => skills.includes(skill));
    const sevenths = seventhQualitySkills.filter((skill) => skills.includes(skill));
    let tqs = utils.clone(triadQualitySkills);
    let sqs = utils.clone(seventhQualitySkills);

    const onlyInversionOrFactor =
      (this.chordSettings.inversion || this.chordSettings.sopranoFactor) &&
      !this.chordSettings.includeQuality;

    // If we're only asking about the inversion or soprano factor, than we shouldn't
    // ask about symmetrical chords since... well, we can't aurally tell which part
    // of the chord is the bass note or soprano factor.
    if (onlyInversionOrFactor) {
      const symmetricals = ['dim_7_chords_et', 'augmented_triad_et'];
      tqs = tqs.filter((s) => !symmetricals.includes(s));
      sqs = sqs.filter((s) => !symmetricals.includes(s));
    }

    const factorSkills = utils.intersection(
      ['triad_soprano_factor_et', 'seventh_chord_soprano_factor'],
      skills
    );
    // If we are ONLY testing the soprano factor, then we need to be sure we also
    // include some other skills so that questions can be generated.
    const onlySopranoFactor = factorSkills.length && factorSkills.length === skills.length;
    if (onlySopranoFactor) {
      skills.push(
        this.chordSettings.chordTypes === 'triads'
          ? 'triad_inversions_et'
          : 'seventh_chord_inversions_et'
      );
    }

    // Make sure we don't just drill one chord type:
    switch (this.chordSettings.chordTypes) {
      case 'triads':
        if (triads.length < 2) skills.push(...tqs);
        break;
      case 'sevenths':
        if (sevenths.length < 2) skills.push(...sqs);
        break;
      case 'both':
        if ([...triads, ...sevenths].length < 2) {
          skills.push(...tqs, ...sqs);
        }
        break;
    }

    return this.qg
      .getChords(skills, this.options.maxQuestions || 10, true)
      .map((qString) => `${qString}/${this.getPlaybackType()}`)
      .map(this.processQuestion, this);
  }

  getPlaybackType(): ChordPlaybackType {
    // if soprano_factor is required, must be open:
    if (this.chordSettings.sopranoFactor) return 'open';
    const skills = this.options.skills!.map(makeString);
    const types = chordPlaybackTypes.filter((type) =>
      skills.find((skill) => skill?.includes(type))
    );
    if (!types.length) return 'byAbility';
    return sample(types) || 'close';
  }

  getPlaybackTypeByAbility(): 'open' | 'close' | 'arp' {
    // If we have no user data, pick at random!
    if (!this.userData) {
      const types: ('open' | 'close' | 'arp')[] = chordPlaybackTypes.filter(
        (t): t is 'open' | 'close' | 'arp' => t !== 'byAbility'
      );
      return sample(types);
    }
    const triadPlaybackSkills: ChordETSkill[] = ['arp_triad_et', 'close_triad_et', 'open_triad_et'];
    const seventhChordPlaybackSkills: ChordETSkill[] = [
      'arp_7_chords_et',
      'close_7_chords_et',
      'open_7_chords_et',
    ];

    const playbackSkills = this.currentQuestion.chord.isSeventh
      ? seventhChordPlaybackSkills
      : triadPlaybackSkills;
    // Find the index of the first skill with less than 50% -- that's the highest trainable one.
    const cutoffIndex = playbackSkills.findIndex((skill) => {
      const userSkill = this.userData!.userStore.knowledgeProgress.skillsById[skill];
      if (!userSkill) return true;
      return userSkill.score <= 50;
    });
    const availablePlaybackSkills = playbackSkills.filter((skill, i) =>
      cutoffIndex === -1 ? true : i <= cutoffIndex
    );
    const priortizedPlaybackSkills = availablePlaybackSkills.reduce(
      (accum: { [skill in ChordETSkill]: number }, skill: string) => {
        // prioritized by how far from 101 -- so always greater than 0 -- they are.
        const priority =
          101 - (this.userData?.userStore.knowledgeProgress.skillsById[skill]?.score || 75);
        return { ...accum, [skill]: priority };
      },
      {} as { [skill in ChordETSkill]: number }
    );
    const playbackSkill = weightedSample(priortizedPlaybackSkills);
    return (
      (['open', 'arp', 'close'] as const).find((pb) => playbackSkill.includes(pb)) ||
      sample(['open', 'arp', 'close'])
    );
  }

  private updateCurrentQuestionByAbility() {
    const playbackType = this.getPlaybackTypeByAbility();
    const noteHolder =
      playbackType === 'open'
        ? this.currentQuestion.chord.getOpenSpacing()
        : this.currentQuestion.chord.noteHolder;
    this.currentQuestion.noteHolder = noteHolder;
    this.currentQuestion.midiMessages = getMidiMessages(noteHolder, playbackType === 'arp');
    this.currentQuestion.skills = getChordETSkills(this.currentQuestion.chord, playbackType, {
      quality: this.chordSettings.includeQuality,
      inversion: this.chordSettings.inversion,
      sopranoFactor: this.chordSettings.sopranoFactor,
    });
  }

  processManualQuestions(questions = this.options.questions): ChordQualityAndInversionETQuestion[] {
    if (!questions || !questions.length) {
      throw Error('Questions must be provided in manual question mode.');
    }
    const processedQuestions = questions.map(this.processQuestion, this);
    return processedQuestions;
  }

  processQuestion(
    questionString: string,
    questionIndex: number
  ): ChordQualityAndInversionETQuestion {
    // Get the playback type:
    const playbackType = chordPlaybackTypes.find((pbType) => questionString.includes(pbType));
    // Now get just the chordName -- we can't just split on / because o/7 includes /.
    const reString = chordPlaybackTypes.map((pb) => `\\/${pb}`).join('|');
    const re = new RegExp(reString, 'g');
    const chordName = questionString.replace(re, '');

    if (!chordName || !playbackType) {
      throw Error(
        `chordQualityAndInverionET questions must be in the format {chordName}/{open|close|arp}. Got ${questionString}`
      );
    }
    const chord = new Chord(chordName);

    const noteHolder = playbackType === 'open' ? chord.getOpenSpacing() : chord.noteHolder;
    const midiMessages = getMidiMessages(noteHolder, playbackType === 'arp');
    const question: Omit<ChordQualityAndInversionETQuestion, 'skills'> = {
      chord,
      spacing: playbackType,
      noteHolder,
      midiMessages,
      text: questionIndex + 1,
    };
    return {
      ...question,
      skills: this.getSkills(question),
    };
  }

  getSkills(question: Omit<ChordQualityAndInversionETQuestion, 'skills'>): ChordETSkill[] {
    return getChordETSkills(question.chord, question.spacing, {
      quality: this.chordSettings.includeQuality,
      inversion: this.chordSettings.inversion,
      sopranoFactor: this.chordSettings.sopranoFactor,
    });
  }

  prepareQuestionPrompt() {
    const elements = [];

    if (this.chordSettings.includeQuality) {
      elements.push('quality');
    }

    if (this.chordSettings.inversion) {
      elements.push(this.chordSettings.inversion === 'figures' ? 'figured bass' : 'inversion');
    }
    if (this.chordSettings.sopranoFactor) {
      elements.push('soprano factor');
    }
    if (elements.length > 1) {
      elements[elements.length - 1] = `and ${elements[elements.length - 1]}`;
    }
    const joiner = elements.length >= 3 ? ', ' : ' ';
    let prompt = `What ${elements.length === 1 ? 'is' : 'are'} the ${elements.join(
      joiner
    )} of the chord?`;

    prompt += "<br /><span id='answerSpan'>_</span>";

    return prompt;
  }

  addListeners() {
    const handler = (value: string | number) => {
      if (value === 'play') this.playQuestion();
      else if (value === 'check') this.checkAnswer();
      else this.updateAnswerDisplay();
    };

    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel!.playbackButtons!.addCallback(handler);
    this.letterPanel!.addCustomButtonCallback(handler, this);
  }

  updateAnswerDisplay() {
    const answerString = this.letterPanel!.chordIdButtons!.answerText;
    const answerSpan = document.getElementById('answerSpan') as HTMLSpanElement;
    if (answerString === '__') {
      answerSpan.removeAttribute('role');
    } else {
      answerSpan.setAttribute('role', 'alert');
    }
    answerSpan.innerHTML = answerString;
  }

  newQuestion() {
    super.newQuestion();
    // It's possible the playback type is set to 'byAbility', in which case
    // we need to regenerate noteHolder, skills and midiMessages:
    if (this.currentQuestion.spacing === 'byAbility') {
      this.updateCurrentQuestionByAbility();
    }
    this.wrongAnswerCount = 0;
    this.letterPanel!.chordIdButtons!.reset();
    this.updateAnswerDisplay();
    this.player!.stop();
    this.playQuestion();
  }

  playQuestion() {
    this.player!.playMidiMessages(this.currentQuestion.midiMessages);
  }

  playAnswer() {} // intentionally blank;

  get answer() {
    return this.letterPanel!.chordIdButtons!.answer;
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;
    const givenChord = this.currentQuestion.chord;

    let figures;
    let inversion;
    let inversionCorrect;
    let qualityCorrect;
    let sopranoFactorCorrect;
    const isSymmetrical = givenChord.quality === 'o7' || givenChord.quality === 'A';
    let seventhMismatch = false;
    const answer = this.answer;
    const incomplete: string[] = [];
    let validFigures = true;

    if (this.chordSettings.includeQuality) {
      if (!answer.quality) {
        incomplete.push('You must first identify the quality of the chord');
      } else {
        const answerQuality = answer.quality + (answer.isSeventh ? '7' : '');
        qualityCorrect = Chord.normalizeQuality(answerQuality).quality === givenChord.quality;
      }
    } else {
      qualityCorrect = true;
    }

    if (this.chordSettings.inversion === 'numbers') {
      if (inversion === '') incomplete.push('You must first provide an inversion.');
      else {
        inversion = answer.inversion;
        inversionCorrect =
          // If the chord is symmetrical, the inversion is right unless they picked 3rd
          // inversion and it's not a seventh chord:
          (isSymmetrical && (inversion === '3' ? givenChord.isSeventh : true)) ||
          // Or, it's right if it's actually right:
          givenChord.inversion === Number(answer.inversion);
      }
    } else if (!this.chordSettings.inversion) {
      inversion = givenChord.inversion;
      inversionCorrect = true;
    }

    if (this.chordSettings.inversion === 'figures') {
      figures = answer.inversion;
      validFigures = Chord.isValidFigures(figures);
      if (validFigures) {
        inversion = Chord.figuresToInversion(figures);
        seventhMismatch = Chord.figuresAreSeventh(figures) !== givenChord.isSeventh;
        inversionCorrect =
          inversion === givenChord.inversion || (isSymmetrical && !seventhMismatch);
      }
      inversion = validFigures ? Chord.figuresToInversion(answer.inversion) : false;
    } else if (this.chordSettings.includeQuality) {
      seventhMismatch = givenChord.isSeventh !== answer.isSeventh;
    }

    if (this.chordSettings.sopranoFactor) {
      if (answer.sopranoFactor) {
        const nh = this.currentQuestion.noteHolder;
        const sop = nh.notes[nh.length - 1];
        sopranoFactorCorrect =
          (isSymmetrical && (answer.sopranoFactor === 'seventh' ? givenChord.isSeventh : true)) ||
          answer.sopranoFactor === givenChord.getFactorFromNote(sop);
      } else {
        incomplete.push('You must first provide a sopranoFactor');
      }
    } else {
      sopranoFactorCorrect = true;
    }

    if (incomplete[0] && !this.options.testMode) {
      return this.checkMark!.warn(incomplete[0]);
    }

    const correct =
      !seventhMismatch &&
      validFigures &&
      qualityCorrect &&
      inversionCorrect &&
      sopranoFactorCorrect;

    if (this.options.testMode) {
      return this.checkpointAnswer(correct);
    }

    if (correct) {
      this.rightAnswer(500);
    } else {
      const hint = [];
      this.wrongAnswerCount++;
      if (this.wrongAnswerCount > 1) {
        if (!validFigures) {
          hint.push('Your figured bass is not valid.');
        }
        if (seventhMismatch) {
          hint.push(`This is ${givenChord.isSeventh ? '' : 'not '}a seventh chord.`);
        } else if (!qualityCorrect) {
          hint.push(`Wrong chord quality.`);
        } else if (!inversionCorrect) {
          hint.push('Wrong inversion.');
        } else if (!sopranoFactorCorrect) {
          hint.push('Wrong soprano factor.');
        }
        this.wrongAnswer(hint[0]);
      } else this.wrongAnswer();
    }
    return undefined;
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct?: boolean) {
    const chord = this.currentQuestion.chord;
    const correctAnswerText = chord.toString();
    const answerSpan = document.getElementById('answerSpan') as HTMLSpanElement;
    const studentAnswerText = answerSpan.innerHTML;

    const answerAndData = {
      question: chord.toString(),
      questionType: 'ChordQualityAndInversionET',
      questionText: this.prepareQuestionPrompt(),
      studentAnswerText,
      correctAnswerText,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }

  static registry: InteractiveRegistryEntry[] = [
    {
      description: 'Identify triad qualities (root position)',
      skills: triadQualitySkills,
    },
    {
      description: 'Identify triad qualities & inversions',
      skills: triadQualitySkills.concat('triad_inversions_et'),
    },
    {
      description: 'Identify seventh chord qualities & inversions',
      skills: seventhQualitySkills.concat('seventh_chord_inversions_et'),
    },
    {
      description: 'Identify soprano factor in triads',
      skills: ['triad_soprano_factor_et'],
      options: {
        chordTypes: false,
      },
    },
    {
      description: 'Identify soprano factor in seventh chords',
      skills: ['seventh_chord_soprano_factor'],
      options: {
        chordTypes: false,
      },
    },
  ];
} // end inherit

function /**
 * getChordETSkills
 *
 * @param {Chord} chord
 * @param {string} spacing 'close', 'open', 'arp'
 * @param {object} options which type of skills to include: {
 *  quality: true,
 *  inversion: true,
 *  sopranoFactor: false,
 * }
 * @returns {Array} [skills]
 */
getChordETSkills(
  chord: Chord,
  spacing: ChordPlaybackType,
  options: {
    quality?: boolean;
    inversion?: ChordQualityAndInversionETOptions['inversion'];
    sopranoFactor?: boolean;
  } = {}
) {
  if (!isObject(options)) throw new Error('getChordETSkills options arg must be an object.');
  options = utils.merge(
    {
      quality: true,
      inversion: true,
      sopranoFactor: false,
    },
    options
  );
  const skills: ChordETSkill[] = [];

  if (options.quality) {
    skills.push(getChordQualityETSkill(chord));
  }

  if (options.inversion) {
    skills.push(chord.isSeventh ? 'seventh_chord_inversions_et' : 'triad_inversions_et');
  }
  if (options.sopranoFactor) {
    skills.push(chord.isSeventh ? 'seventh_chord_soprano_factor' : 'triad_soprano_factor_et');
  }

  switch (spacing) {
    case 'open':
      skills.push(chord.isSeventh ? 'open_7_chords_et' : 'open_triad_et');
      break;
    case 'close':
      skills.push(chord.isSeventh ? 'close_7_chords_et' : 'close_triad_et');
      break;
    case 'arp':
      skills.push(chord.isSeventh ? 'arp_7_chords_et' : 'arp_triad_et');
      break;
  }
  return skills;
}

const chordQualityToETSkill: Record<ChordQualityName, TriadETSkill | SeventhChordETSkill> = {
  '7': 'dom_7_chords_et',
  M7: 'maj_7_chords_et',
  m7: 'min_7_chords_et',
  'o/7': 'half_dim_7_chords_et',
  o7: 'dim_7_chords_et',
  M: 'major_triad_et',
  m: 'minor_triad_et',
  o: 'diminshed_triad_et',
  A: 'augmented_triad_et',
} as const;

function getChordQualityETSkill(chord: Chord): TriadETSkill | SeventhChordETSkill {
  return chordQualityToETSkill[chord.quality];
}

function getMidiMessages(noteHolder: NoteHolder, isArpeggiated: boolean): MidiMessage[] {
  const midiMessages = !isArpeggiated
    ? noteHolder
        .clone()
        .sort()
        .getMidiMessages()
        .map((midiMessage, i, arr) => {
          if (i === arr.length - 1) {
            midiMessage.velocity = 1;
          } else if (i === 0) {
            midiMessage.velocity = 0.9;
          } else {
            midiMessage.velocity = 0.7;
          }
          return midiMessage;
        })
    : noteHolder
        .getMidiMessages()
        .map((message, index, arr) => {
          message.startBeat = index;
          message.stopBeat = index === arr.length - 1 ? index + 3 : index + 1;
          return message;
        })
        .concat(
          noteHolder.getMidiMessages().map((message, index, arr) => {
            message.startBeat = arr.length + 1;
            message.stopBeat = arr.length + 4;
            message.velocity = 0.6;
            return message;
          })
        );
  return midiMessages;
}
